import { forwardRef, Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import cx from 'classnames';
import { IntegerInput } from '../__internal__/IntegerInput';
import { useFormatLocale } from '../../../locale/formatting/useFormatLocale';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { postfixTestId } from '../../../util/testId';
import { Select } from '../../Select';
import { EPOCH, getDayPeriodOrder, getDayPeriods, getHoursSeparator, getMinutesSeparator, getSecondsSeparator, GMT_OFFSET_ADJUSTMENT, GMT_OFFSET_MINUTES, GMT_OFFSET_SECONDS, inDaylightTime, isSameDay, MAX_HOURS, MAX_MINUTES, MIN_HOURS, MIN_MINUTES, NEGATIVE_HOURS_THRESHOLD, NEGATIVE_MINUTES_THRESHOLD } from '../DateUtil';
import './TimePicker.css';
import cn from './TimePicker.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_DATE = EPOCH;
const DEFAULT_STEP = 1;
export const TimePicker = /*#__PURE__*/forwardRef(function TimePicker(props, ref) {
  // FIXME: these dropdowns probably need to take a non-visual label
  const localize = useNCTextLocalizer('DatePicker');
  const lang = useFormatLocale();
  const dayPeriodOrder = useMemo(() => getDayPeriodOrder(lang), [lang]);
  const dayPeriods = useMemo(() => getDayPeriods(lang), [lang]);
  const fractionalSecondSeparator = useMemo(() => getFractionalSecondSeparator(lang), [lang]);
  const secondsSeparator = useMemo(() => getSecondsSeparator(lang), [lang]);
  const hoursStep = Math.max(DEFAULT_STEP, props.hoursStep || DEFAULT_STEP);
  const minutesStep = Math.max(DEFAULT_STEP, props.minutesStep || DEFAULT_STEP);
  const secondsStep = Math.max(DEFAULT_STEP, props.secondsStep || DEFAULT_STEP);
  const h24clock = props.show24HourClock ?? dayPeriodOrder === 'none';
  const hourItems = useMemo(() => h24clock ? mapRange(24, hoursStep, i => ({
    text: String(i).padStart(2, '0')
  })) : mapRange(12, hoursStep, i => ({
    text: String(i + 1)
  })), [h24clock, hoursStep]);
  const minuteItems = mapRange(60, minutesStep, i => ({
    text: String(i).padStart(2, '0')
  }));
  const secondItems = mapRange(60, secondsStep, i => ({
    text: String(i).padStart(2, '0')
  }));
  const getHourDurationValue = () => {
    return calculateHourDurationValue(props.value);
  };
  const getMinuteDurationValue = () => {
    return calculateMinuteDurationValue(props.value, props.showHoursDuration, props.showMinutesDuration);
  };
  const getSecondsValue = useCallback(() => {
    if (props.roundSeconds && !props.showMilliseconds) {
      if (props.value.getMilliseconds() >= 500) {
        return props.value.getSeconds() + 1;
      }
    }
    return props.value.getSeconds();
  }, [props.roundSeconds, props.showMilliseconds, props.value]);
  const getMillisecondsDurationValue = () => {
    return calculateMillisecondsDurationValue(props.value, props.showHoursDuration, props.showMinutesDuration);
  };
  const getStepIndex = useCallback((type, items) => {
    const index = items.map(element => element.text).indexOf(type === 'hours' ? (props.value.getHours() % 12).toString() : type === 'minutes' ? props.value.getMinutes().toString().padStart(2, '0') : getSecondsValue().toString().padStart(2, '0'));
    if (index === -1) {
      const nextValue = new Date(props.value);
      if (type === 'hours') {
        let nextHour = parseInt(hourItems[0].text);
        if (nextValue.getHours() >= 12) {
          nextHour += 12;
        }
        nextValue.setHours(nextHour);
      }
      if (type === 'minutes') {
        nextValue.setMinutes(parseInt(minuteItems[0].text));
      }
      if (type === 'seconds') {
        nextValue.setSeconds(parseInt(secondItems[0].text));
      }
      props.onChange(nextValue);
    }
    return index !== -1 ? index : 0;
  }, [props, hourItems, minuteItems, secondItems, getSecondsValue]);

  // NOTE: (hoursIndex - 1) % 12 is the inverse logic of onSelect for 12hr clocks, but
  //     | JS returns a remainder instead of a modulo.
  // SEE: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Remainder
  //    > To obtain a modulo in JavaScript, in place of a % n, use ((a % n ) + n ) % n.
  let hourIndex = h24clock ? props.value.getHours() : props.hoursStep ? getStepIndex('hours', hourItems) : (props.value.getHours() + 11) % 12;
  const refHoursInput = useRef(null);
  const refMinutesInput = useRef(null);
  const refMillisecondsInput = useRef(null);
  const containerRef = useRef(null);
  useEffect(() => {
    // check if the timepicker rendered next to another picker or alone to adjust a focus
    if (!containerRef.current?.previousSibling) {
      setTimeout(() => {
        refHoursInput.current?.focus();
        refHoursInput.current?.setAttribute('data-autofocus', 'true');
      });
    }
  }, [ref]);

  // Using useRef because it's persisted between renders. Initially tried useState
  // and encountered a situation where the state had not been updated at the time I was checking the value.
  const isMinutesDisabled = useRef(false);
  const isSecondsDisabled = useRef(false);
  // Also tried using useCallback here but it broke the functionality, whereas useRef didn't
  const hoursSelectedIndex = useMemo(() => hourIndex, [hourIndex]); // useRef(hourIndex);
  const minutesSelectedIndex = useRef(props.showHoursDuration || props.showMinutesDuration ? calculateMinuteDurationValue(props.value, props.showHoursDuration, props.showMinutesDuration) : props.minutesStep ? getStepIndex('minutes', minuteItems) : props.value.getMinutes());
  const secondsSelectedIndex = useRef(props.showHoursDuration || props.showMinutesDuration ? calculateSecondDurationValue(props.value, props.showHoursDuration, props.showMinutesDuration) : props.secondsStep ? getStepIndex('seconds', secondItems) : getSecondsValue());
  const dayPeriodElement = !h24clock && /*#__PURE__*/_jsx(Select, {
    testId: postfixTestId(props.testId, 'dayPeriodSelect'),
    popoverClassName: props.selectClassName,
    values: [{
      text: dayPeriods[0]
    }, {
      text: dayPeriods[1]
    }],
    selectedIndex: Math.floor(props.value.getHours() / 12),
    onSelected: selectedIndex => {
      let nextHour = props.value.getHours() % 12;
      if (selectedIndex === 1) {
        nextHour += 12;
      }
      const nextValue = new Date(props.value);
      nextValue.setHours(nextHour);
      props.onChange(nextValue);
    },
    ariaLabel: localize('period.aria'),
    style: {
      minWidth: 'auto'
    }
  });
  const getDurationDate = () => {
    const hoursDuration = props.showHoursDuration;
    const minutesDuration = props.showMinutesDuration;
    const dateValue = new Date(DEFAULT_DATE);
    let hours = 0;
    let negativeDurations = false;
    if (hoursDuration) {
      if (refHoursInput.current) {
        hours = parseInt(refHoursInput.current?.value, 10);
      }
    } else if (props.showHours) {
      hours = hoursSelectedIndex;
    }
    if (hours !== null) {
      dateValue.setHours(hours);
      if (hours < 0 || 1 / hours === -Infinity /* negative zero */) {
        negativeDurations = true;
      }
      // if the negative value is less than -754,907, i.e -754,908, then the date object
      // is off by 3 minutes 58 seconds for GMT-0456.  We need to add the 3:58.
      if (hours < NEGATIVE_HOURS_THRESHOLD) {
        dateValue.setMinutes(GMT_OFFSET_MINUTES);
        dateValue.setSeconds(GMT_OFFSET_SECONDS);
      }
    }
    if (minutesDuration) {
      if (refMinutesInput.current) {
        const minutes = parseInt(refMinutesInput.current?.value, 10);
        if (minutes < 0 || 1 / minutes === -Infinity /* negative zero */) {
          negativeDurations = true;
        }
        dateValue.setMinutes(minutes);
      }
    } else {
      if (negativeDurations) {
        dateValue.setMinutes(dateValue.getMinutes() - minutesSelectedIndex.current);
      } else {
        dateValue.setMinutes(minutesSelectedIndex.current);
      }
    }
    if (props.showSeconds) {
      if (negativeDurations) {
        dateValue.setSeconds(dateValue.getSeconds() - secondsSelectedIndex.current);
      } else {
        dateValue.setSeconds(secondsSelectedIndex.current);
      }
    }
    if (props.showMilliseconds) {
      const milliseconds = parseInt(refMillisecondsInput.current?.value, 10);
      if (negativeDurations) {
        dateValue.setMilliseconds(dateValue.getMilliseconds() - milliseconds);
      } else {
        dateValue.setMilliseconds(milliseconds);
      }
    }

    // if this puts a duration in daylight savings time, add an hour
    if (hoursDuration || minutesDuration) {
      if (inDaylightTime(dateValue)) {
        dateValue.setHours(dateValue.getHours() + 1);
      }
    }
    return dateValue;
  };
  return /*#__PURE__*/_jsxs("div", {
    className: cn.container,
    ref: containerRef,
    children: [!props.showHoursDuration && !props.showMinutesDuration && props.showDayPeriod && dayPeriodOrder === 'before' && /*#__PURE__*/_jsxs(Fragment, {
      children: [dayPeriodElement, /*#__PURE__*/_jsx(Separator, {})]
    }), props.showHoursDuration ? /*#__PURE__*/_jsx(IntegerInput, {
      ref: ref,
      inputRef: refHoursInput,
      testId: postfixTestId(props.testId, 'hoursDurationInput'),
      className: cn.hours,
      value: getHourDurationValue(),
      minValue: MIN_HOURS,
      maxValue: MAX_HOURS,
      onChange: (hours, reason) => {
        if (isNaN(hours)) return;
        if (Math.abs(hours) === MAX_HOURS) {
          minutesSelectedIndex.current = 0;
          secondsSelectedIndex.current = 0;
          isMinutesDisabled.current = true;
          isSecondsDisabled.current = true;
        } else {
          isMinutesDisabled.current = false;
          isSecondsDisabled.current = false;
        }
        if (reason === 'onCommit') {
          const nextValue = getDurationDate();
          props.onChange(nextValue);
        }
      },
      ariaLabel: localize('hours.aria'),
      isDuration: true
    }) : props.showHours ? /*#__PURE__*/_jsx(Select, {
      selectBoxRef: refHoursInput,
      popoverClassName: props.selectClassName,
      testId: postfixTestId(props.testId, 'hourSelect'),
      values: hourItems,
      selectedIndex: hoursSelectedIndex,
      onSelected: selectedIndex => {
        hourIndex = selectedIndex ?? 0;
        let nextHour = props.hoursStep ? parseInt(hourItems[selectedIndex].text) : selectedIndex ?? 0;
        if (!h24clock) {
          if (!props.hoursStep) {
            // NOTE: we add 1 because 12 hour clocks start at 12 (so (11 + 1) % 12 => 0)
            nextHour = (nextHour + 1) % 12;
          }
          if (props.value.getHours() >= 12) {
            nextHour += 12;
          }
        }
        const nextValue = new Date(props.value);
        nextValue.setHours(nextHour);
        props.onChange(nextValue);
      },
      ariaLabel: localize('hours.aria'),
      style: {
        minWidth: 'auto'
      }
    }) : null, props.showHours && props.showMinutes ? /*#__PURE__*/_jsx(Separator, {
      className: cx(lang.toLocaleLowerCase() === 'fr-ca' && cn.frCaLocale),
      text: getHoursSeparator(lang)
    }) : null, props.showMinutesDuration ? /*#__PURE__*/_jsx(IntegerInput, {
      ref: ref,
      inputRef: refMinutesInput,
      testId: postfixTestId(props.testId, 'minutesDurationInput'),
      className: cn.minutes,
      value: getMinuteDurationValue(),
      minValue: MIN_MINUTES,
      maxValue: MAX_MINUTES,
      onChange: (minutes, reason) => {
        if (isNaN(minutes)) return;
        if (Math.abs(minutes) === MAX_MINUTES) {
          secondsSelectedIndex.current = 0;
          isSecondsDisabled.current = true;
        } else {
          isSecondsDisabled.current = false;
        }
        if (reason === 'onCommit') {
          const nextValue = getDurationDate();
          props.onChange(nextValue);
        }
      },
      ariaLabel: localize('minutes.aria'),
      isDuration: true
    }) : props.showMinutes ? /*#__PURE__*/_jsx(Select, {
      testId: postfixTestId(props.testId, 'minuteSelect'),
      popoverClassName: props.selectClassName,
      values: minuteItems,
      selectedIndex: minutesSelectedIndex.current,
      onSelected: selectedIndex => {
        minutesSelectedIndex.current = selectedIndex ?? 0;
        const nextValue = props.showHoursDuration ? getDurationDate() : new Date(props.value);
        if (!props.showHoursDuration) {
          props.minutesStep ? nextValue.setMinutes(parseInt(minuteItems[selectedIndex].text)) : nextValue.setMinutes(selectedIndex ?? 0);
        }
        props.onChange(nextValue);
      },
      ariaLabel: localize('minutes.aria'),
      style: {
        minWidth: 'auto'
      },
      disabled: isMinutesDisabled.current
    }) : null, props.showSeconds && (props.showHours || props.showMinutes) ? /*#__PURE__*/_jsx(Separator, {
      className: cx(lang.toLocaleLowerCase() === 'fr-ca' && cn.frCaLocale),
      text: getMinutesSeparator(lang)
    }) : null, props.showSeconds ? /*#__PURE__*/_jsx(Select, {
      testId: postfixTestId(props.testId, 'secondSelect'),
      popoverClassName: props.selectClassName,
      values: secondItems,
      selectedIndex: secondsSelectedIndex.current,
      onSelected: selectedIndex => {
        secondsSelectedIndex.current = selectedIndex ?? 0;
        const nextValue = props.showHoursDuration || props.showMinutesDuration ? getDurationDate() : new Date(props.value);
        if (!props.showHoursDuration && !props.showMinutesDuration) {
          props.minutesStep ? nextValue.setSeconds(parseInt(secondItems[selectedIndex].text)) : nextValue.setSeconds(selectedIndex ?? 0);
        }
        props.onChange(nextValue);
      },
      ariaLabel: localize('seconds.aria'),
      style: {
        minWidth: 'auto'
      },
      disabled: isSecondsDisabled.current
    }) : null, props.showSeconds && secondsSeparator.trim().length > 0 ? /*#__PURE__*/_jsx(Separator, {
      className: cx(lang.toLocaleLowerCase() === 'fr-ca' && cn.frCaLocale),
      text: secondsSeparator
    }) : null, props.showMilliseconds && props.showSeconds && /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(Separator, {
        text: fractionalSecondSeparator
      }), /*#__PURE__*/_jsx(IntegerInput, {
        ref: ref,
        inputRef: refMillisecondsInput,
        testId: postfixTestId(props.testId, 'millisecondsInput'),
        className: cn.milliseconds,
        value: props.showHoursDuration || props.showMinutesDuration ? getMillisecondsDurationValue() : props.value.getMilliseconds(),
        minValue: 0,
        maxValue: 999,
        onChange: milliseconds => {
          if (isNaN(milliseconds)) return;
          const nextValue = new Date(props.value);
          nextValue.setMilliseconds(milliseconds);
          props.onChange(nextValue);
        },
        ariaLabel: localize('milliseconds.aria')
      })]
    }), !props.showHoursDuration && !props.showMinutesDuration && props.showDayPeriod && dayPeriodOrder === 'after' && /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(Separator, {}), dayPeriodElement]
    })]
  });
});
const Separator = ({
  text,
  className
}) => /*#__PURE__*/_jsx("span", {
  className: cx(cn.separator, className),
  children: text ? ` ${text} ` : null
});
function mapRange(length, step, map) {
  const result = [];
  for (let i = 0; i < length; ++i) {
    if (i % step === 0) {
      result.push(map(i));
    }
  }
  return result;
}
export function getFractionalSecondSeparator(localeTag) {
  // FIXME: eventually, DateTimeFormat will support a fractionalSecondDigits option on our browser target.
  //      | this will replace the need for using NumberFormat like this.
  // NOTE: portable reads the decimal separator for numbers from cldr data and uses it for the ms separator.
  //     | This pretty much does the same thing.
  const parts = new Intl.NumberFormat(localeTag).formatToParts(7.7);
  const decimalPart = parts.find(value => value.type === 'decimal');
  return decimalPart?.value ?? '.';
}
export const calculateHourDurationValue = date => {
  const currDate = new Date(date);
  const today = new Date();
  if (isSameDay(currDate, today)) {
    return 0;
  }
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const dateDiff = date.valueOf() - DEFAULT_DATE.valueOf();
  const negativeDurations = dateDiff < 0;
  hours = calculateHours(dateDiff, hours, minutes, negativeDurations, currDate);
  return hours;
};
export const calculateMinuteDurationValue = (date, includeHoursDuration, includeMinutesDuration) => {
  const currDate = new Date(date);
  const today = new Date();
  if (isSameDay(currDate, today)) {
    return 0;
  }
  let hours = includeHoursDuration ? calculateHourDurationValue(date) : date ? date.getHours() : null;
  let minutes = date.getMinutes();
  const seconds = date.getSeconds();
  const dateDiff = date.valueOf() - DEFAULT_DATE.valueOf();
  const negativeDurations = dateDiff < 0;
  if (includeHoursDuration && !includeMinutesDuration) {
    hours = calculateHours(dateDiff, hours, null, negativeDurations, currDate);
  } else {
    minutes = calculateMinutes(dateDiff, minutes, seconds, negativeDurations);
  }
  if (includeHoursDuration && (hours === MAX_HOURS || hours === MIN_HOURS)) return 0;
  if (!includeHoursDuration && includeMinutesDuration) return minutes;
  minutes = adjustMinutes(currDate, negativeDurations, minutes, seconds);
  return minutes;
};
export const calculateSecondDurationValue = (date, includeHoursDuration, includeMinutesDuration) => {
  const currDate = new Date(date);
  const today = new Date();
  if (isSameDay(currDate, today)) {
    return 0;
  }
  let hours = includeHoursDuration ? calculateHourDurationValue(date) : null;
  let minutes = includeHoursDuration || includeMinutesDuration ? calculateMinuteDurationValue(date, includeHoursDuration, includeMinutesDuration) : null;
  const dateDiff = date.valueOf() - DEFAULT_DATE.valueOf();
  const negativeDurations = dateDiff < 0;
  if (includeHoursDuration && !includeMinutesDuration) {
    hours = calculateHours(dateDiff, hours, null, negativeDurations, currDate);
  } else {
    minutes = calculateMinutes(dateDiff, minutes, date.getSeconds(), negativeDurations);
  }
  if (includeHoursDuration && (hours === MAX_HOURS || hours === MIN_HOURS)) return 0;
  if (includeMinutesDuration && (minutes === MAX_MINUTES || minutes === MIN_MINUTES)) return 0;
  const seconds = adjustSeconds(currDate, negativeDurations, date.getSeconds());
  return seconds;
};
export const calculateMillisecondsDurationValue = (date, includeHoursDuration, includeMinutesDuration) => {
  const currDate = new Date(date);
  const today = new Date();
  if (isSameDay(currDate, today)) {
    return 0;
  }
  const dateDiff = date.valueOf() - DEFAULT_DATE.valueOf();
  const negativeDurations = dateDiff < 0;
  let hours = includeHoursDuration ? calculateHourDurationValue(date) : null;
  let minutes = includeHoursDuration || includeMinutesDuration ? calculateMinuteDurationValue(date, includeHoursDuration, includeMinutesDuration) : null;
  const seconds = calculateSecondDurationValue(date, includeHoursDuration, includeMinutesDuration);
  if (includeHoursDuration && !includeMinutesDuration) {
    hours = calculateHours(dateDiff, hours, null, negativeDurations, currDate);
  } else {
    minutes = calculateMinutes(dateDiff, minutes, seconds, negativeDurations);
  }
  if (includeHoursDuration && (hours === MAX_HOURS || hours === MIN_HOURS)) return 0;
  // Check if the minutes are at the max value of 4228629119 or min value of -4228629119, if so return 0
  if (includeMinutesDuration && (minutes === MAX_MINUTES || minutes === MIN_MINUTES)) return 0;
  const milliseconds = adjustMilliseconds(currDate, negativeDurations, date.getMilliseconds());
  return milliseconds;
};
const calculateHours = (dateDiff, hours, minutes, negativeDurations, currDate) => {
  if (negativeDurations) {
    if (minutes !== null) {
      if (minutes !== 0 && minutes < 30) {
        // Need to use Math.trunc here because if it's a number like -2.51 Math.round rounds to -3
        hours = Math.trunc(dateDiff / 3600000);
      } else {
        hours = Math.floor(Math.round(dateDiff / 3600000));
      }
    }
    // if the negative value is less than -754,907, i.e -754,908, then the date object
    // is off by 3 minutes 58 seconds for GMT-0456.  We need to add the 3:58.
    if (hours !== null && hours < NEGATIVE_HOURS_THRESHOLD) {
      currDate.setMinutes(currDate.getMinutes() - GMT_OFFSET_MINUTES);
      currDate.setSeconds(currDate.getSeconds() - GMT_OFFSET_SECONDS);
    }
  } else {
    hours = Math.floor(dateDiff / 3600000);
  }
  return hours;
};
const calculateMinutes = (dateDiff, minutes, seconds, negativeDurations) => {
  if (negativeDurations) {
    if (seconds !== null && seconds !== 0 && seconds < 30) {
      // Need to use Math.trunc here because if it's a number like -2.51 Math.round rounds to -3
      minutes = Math.trunc(dateDiff / 60000);
    } else {
      minutes = Math.floor(Math.round(dateDiff / 60000));
    }
    // if the negative value is less than -45,294,476, i.e -45,294,477, then the miutes
    // are off by 4 minutesfor GMT-0456.  We need to add the 4.
    if (minutes < NEGATIVE_MINUTES_THRESHOLD) {
      minutes = minutes + GMT_OFFSET_ADJUSTMENT;
    }
  } else {
    minutes = Math.floor(dateDiff / 60000);
  }
  return minutes;
};
const adjustMinutes = (currDate, negativeDurations, minutes, seconds) => {
  // If negative durations and there are seconds, need to adjust the time back
  // for minutes
  if (negativeDurations && seconds !== 0) {
    currDate.setMinutes(60 + currDate.getMinutes());
    currDate.setSeconds(60 + currDate.getSeconds());
  }
  if (negativeDurations && currDate.getMinutes() !== 0) {
    minutes = 60 - currDate.getMinutes();
  } else {
    minutes = currDate.getMinutes();
  }
  return minutes;
};
const adjustSeconds = (currDate, negativeDurations, seconds) => {
  // If negative durations and there are seconds, need to adjust the time back
  // for minutes
  if (negativeDurations && seconds !== 0) {
    currDate.setMinutes(60 + currDate.getMinutes());
    currDate.setSeconds(60 + currDate.getSeconds());
  }
  if (negativeDurations && currDate.getSeconds() !== 0) {
    seconds = 60 - currDate.getSeconds();
  } else {
    seconds = currDate.getSeconds();
  }
  return seconds;
};
const adjustMilliseconds = (currDate, negativeDurations, milliseconds) => {
  // If negative durations and there are seconds, need to adjust the time back
  // for minutes
  if (negativeDurations && milliseconds !== 0) {
    currDate.setMinutes(60 + currDate.getMinutes());
    currDate.setSeconds(60 + currDate.getSeconds());
    currDate.setMilliseconds(999 + currDate.getSeconds());
  }
  if (negativeDurations && currDate.getMilliseconds() !== 0) {
    milliseconds = 999 - currDate.getMilliseconds();
  } else {
    milliseconds = currDate.getMilliseconds();
  }
  return milliseconds;
};
//# sourceMappingURL=TimePicker.js.map